import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './CreateStudyMission.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Componente para criar Missão de Estudo (Responsável)
 */
function CreateStudyMission({ childId, onSuccess }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pega childId da URL se não for passado como prop
  const effectiveChildId = childId || searchParams.get('childId');
  
  const [formData, setFormData] = useState({
    child_id: effectiveChildId || '',
    subject: 'Português',
    title: '',
    content_type: 'TEXT',
    content_text: '',
    detail_level: 'INTERMEDIATE',
    max_points: 100,
    exam_date: ''
  });
  
  // Atualiza child_id quando mudar
  useEffect(() => {
    if (effectiveChildId) {
      setFormData(prev => ({
        ...prev,
        child_id: effectiveChildId
      }));
    }
  }, [effectiveChildId]);
  
  const [pdfFile, setPdfFile] = useState(null);
  
  const subjects = [
    'Português', 'Matemática', 'História', 'Geografia',
    'Ciências', 'Inglês', 'Artes', 'Educação Física', 'Filosofia'
  ];
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Arquivo PDF muito grande (máximo 5MB)');
        return;
      }
      setPdfFile(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const token = localStorage.getItem('access_token'); // Corrigido: era 'token', agora é 'access_token'
      
      // Validações
      if (!formData.child_id) {
        throw new Error('ID da criança não foi fornecido');
      }
      
      if (!formData.title.trim()) {
        throw new Error('Título é obrigatório');
      }
      
      if (formData.content_type === 'TEXT' && !formData.content_text.trim()) {
        throw new Error('Conteúdo é obrigatório');
      }
      
      if (formData.content_type === 'PDF' && !pdfFile) {
        throw new Error('Selecione um arquivo PDF');
      }
      
      // Prepara dados para envio
      const data = new FormData();
      
      // Adiciona apenas campos não vazios
      data.append('child_id', formData.child_id);
      data.append('subject', formData.subject);
      data.append('title', formData.title);
      data.append('content_type', formData.content_type);
      data.append('detail_level', formData.detail_level);
      data.append('max_points', formData.max_points);
      
      if (formData.content_type === 'TEXT') {
        data.append('content_text', formData.content_text);
      }
      
      if (formData.exam_date) {
        data.append('exam_date', formData.exam_date);
      }
      
      if (pdfFile) {
        data.append('content_pdf', pdfFile);
      }
      
      console.log('=== DEBUG FRONTEND ===');
      console.log('Token:', token);
      console.log('FormData keys:', Array.from(data.keys()));
      console.log('child_id:', formData.child_id);
      
      const response = await axios.post(
        `${API_URL}/study-missions`,
        data,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Resposta 202 Accepted = processamento assíncrono iniciado
      if (response.status === 202 && response.data.mission_id) {
        // Redireciona para tela de loading com polling
        navigate(`/parent/study-missions/${response.data.mission_id}/loading`);
      } else if (response.data.study_mission) {
        // Fallback para resposta síncrona (retrocompatibilidade)
        alert('Missão de Estudo criada com sucesso!');
        if (onSuccess) {
          onSuccess(response.data.study_mission);
        } else {
          navigate('/parent-dashboard');
        }
      }
    } catch (err) {
      console.error('Erro ao criar missão:', err);
      setError(err.response?.data?.error || err.message || 'Erro ao criar missão');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="create-study-mission">
      <h2>📘 Criar Missão de Estudo</h2>
      
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}
      
      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <h3>🤖 Gerando Missão de Estudo</h3>
            <p>A IA está criando o conteúdo educacional...</p>
            <p className="loading-note">Isso pode levar até 1 minuto. Por favor, aguarde.</p>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Matéria *</label>
          <select
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            required
          >
            {subjects.map(subject => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label>Título da Missão *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Ex: Prova de Português - Verbos"
            required
          />
        </div>
        
        <div className="form-group">
          <label>Tipo de Conteúdo *</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="content_type"
                value="TEXT"
                checked={formData.content_type === 'TEXT'}
                onChange={handleInputChange}
              />
              Texto
            </label>
            <label>
              <input
                type="radio"
                name="content_type"
                value="PDF"
                checked={formData.content_type === 'PDF'}
                onChange={handleInputChange}
              />
              PDF
            </label>
          </div>
        </div>
        
        {formData.content_type === 'TEXT' ? (
          <div className="form-group">
            <label>Conteúdo da Prova *</label>
            <textarea
              name="content_text"
              value={formData.content_text}
              onChange={handleInputChange}
              rows={10}
              placeholder="Cole o conteúdo ou tema da prova (ex: 'Biografia e Autobiografia')..."
              required
            />
            <small>Pode ser um tema simples ou conteúdo completo</small>
          </div>
        ) : (
          <div className="form-group">
            <label>Arquivo PDF *</label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              required
            />
            <small>Máximo 5MB, até 10 páginas</small>
          </div>
        )}
        
        <div className="form-group">
          <label>Nível de Detalhamento *</label>
          <select
            name="detail_level"
            value={formData.detail_level}
            onChange={handleInputChange}
            required
          >
            <option value="ESSENTIAL">Essencial - Resumido</option>
            <option value="INTERMEDIATE">Intermediário - Com Exemplos</option>
            <option value="DETAILED">Detalhado - Aprofundado</option>
          </select>
        </div>
        
        <div className="form-group">
          <label>Pontos Máximos</label>
          <input
            type="number"
            name="max_points"
            value={formData.max_points}
            onChange={handleInputChange}
            min="50"
            max="200"
          />
          <small>HeroCoins que a criança pode ganhar (50-200)</small>
        </div>
        
        <div className="form-group">
          <label>Data da Prova (opcional)</label>
          <input
            type="date"
            name="exam_date"
            value={formData.exam_date}
            onChange={handleInputChange}
          />
        </div>
        
        <div className="form-actions">
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? 'Criando...' : '✅ Criar Missão'}
          </button>
          <button 
            type="button" 
            onClick={() => navigate(-1)} 
            className="btn btn-secondary"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateStudyMission;
